import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CollectApiServiceBase } from './collect-api-base.service';
import { FolderTree } from '../models/folder-tree.model';

import { plainToClass } from "class-transformer";
import { Logging } from '../logging';
import { FolderFormInstanceTree } from '../models/folder-form-instance-tree.model';
import { ProgressIndicatorService } from './progress-indicator.service';
import { Observable } from 'rxjs';
import { Folder } from '../models/site-content/folder.model';
import { FormInstance } from '../models/site-content/form-instance.model';

@Injectable({
    providedIn: 'root'
})
export class FolderFormInstanceTreeService extends CollectApiServiceBase<FolderFormInstanceTree> {

    // pharv - 1/4/2023 - adding these flags here in order to make tree and structure components
    // simple and decoupled from each other
    private pinTreeOpen: boolean;
    private showTree: boolean;

    constructor(http: HttpClient, progressIndicatorService: ProgressIndicatorService) {
        super(http, progressIndicatorService, environment.apiUrl, 'folderTree', FolderFormInstanceTree)
    }

    formatResponse(data: FolderFormInstanceTree): FolderFormInstanceTree {
        let obj = FolderFormInstanceTreeService.doFormatResponse(data);

        return (obj);
    }

    private static doFormatResponse(data: FolderFormInstanceTree): FolderFormInstanceTree {
        let obj = plainToClass(FolderFormInstanceTree, data);

        return (obj);
    }

    public getTree(siteId: number, includeForms: boolean = false, reload: boolean = false): Promise<FolderFormInstanceTree> {
        let url = `${this.url}/${this.endpoint}/GetTreeWithPermissions/${siteId}/?includeForms=${includeForms}`;
        if (reload) {
            url = `${url}&reload=true`;
            this.progressIndicatorService.completed50Percent("Refreshing Tree...");
        }

        return this.http.get<FolderFormInstanceTree>(url)
            .toPromise()
            .then(response => {
                let tree = this.formatResponse(response);
                if (reload) this.progressIndicatorService.completed100Percent("Refreshed Tree");
                return tree;
            })
            .catch(this.handleError);
    }

    // The following "pinned" and "show" related methods moved here from
    // Tree and Structure components where they were scattered
    public get TreeIsPinnedOpen(): boolean {
        return this.pinTreeOpen;
    }

    public get ShowTree(): boolean {
        return this.showTree;
    }

    public set ShowTree(val: boolean) {
        //console.log('Serv. set ShowTree');
        this.showTree = false;
    }

    public toggleTreePinnedState(): boolean {
        let pinOpen = localStorage.getItem('pinTreeOpen');
        if (pinOpen == null || pinOpen == 'false') {
            this.pinTreeOpen = true;
            this.showTree = true;
            localStorage.setItem('pinTreeOpen', 'true');
        } else {
            this.pinTreeOpen = false;
            this.showTree = false;
            localStorage.setItem('pinTreeOpen', 'false');
        }
        return pinOpen == 'true';
    }

    public toggleShowTree() {
        this.showTree = !this.showTree;
    }

    public openTreeIfPinned(): void {
        let pinOpen = localStorage.getItem('pinTreeOpen');
        if (pinOpen == 'true') {
            this.showTree = true;
            this.pinTreeOpen = true;
        }
    }

    public getTreeAsObservable(siteId: number, includeForms: boolean, reload: boolean = false): Observable<FolderFormInstanceTree> {
        let url = `${this.url}/${this.endpoint}/GetTreeWithPermissions/${siteId}/?includeForms=${includeForms}`;
        if (reload) this.progressIndicatorService.completed50Percent("Refreshing Tree...");

        return this.http.get<FolderFormInstanceTree>(url);
    }

    public static unpackTree(data: FolderFormInstanceTree): FolderFormInstanceTree {
        let obj = FolderFormInstanceTreeService.doFormatResponse(data);

        return (obj);
    }

    public getTreeFor(siteId: number, userOrGroupId: string): Promise<FolderFormInstanceTree> {
        let url = `${this.url}/${this.endpoint}/GetTreeWithPermissions/${siteId}/${userOrGroupId}`;
        this.progressIndicatorService.completed50Percent("Reloading Tree with selected user's permissions...");
        return this.http.get<FolderFormInstanceTree>(url)
            .toPromise()
            .then(response => {
                let tree = this.formatResponse(response);
                this.progressIndicatorService.completed100Percent("Tree reloaded with selected user's permissions");
                return tree;
            })
            .catch(this.handleError);
    }

    public save(data: FolderFormInstanceTree): Promise<FolderFormInstanceTree> {
        //console.log(data.id);
        if (data.id > 0) {
            return (this.update(data));
        } else
            return (this.create(data));
    }
}

export class FolderFormInstanceTreeManager {
    // Properties.
    private treeObservable: Observable<FolderFormInstanceTree> = null;
    private tree: FolderFormInstanceTree = null;

    // Constructor.
    public constructor(treeObservable: Observable<FolderFormInstanceTree>) {
        this.treeObservable = treeObservable; 
        this.treeObservable.subscribe(response => {
            let unpackedTree: FolderFormInstanceTree = FolderFormInstanceTreeService.unpackTree(response);

            this.tree = unpackedTree;
        });

        return;
    }

    // Getter methods.
    public getTree(): Observable<FolderFormInstanceTree> {
        let result: Observable<FolderFormInstanceTree> = null;

        if (this.tree != null) {
            result = Observable.create(function (observer: any) {
                observer.next(this.tree);
            });
        } else {
            result = this.treeObservable;
        }

        return (result);
    }

    // Note:  as getFolderWithCallback() is the easiest to call, it is
    //        presently being used within the structure component class.
    public getFolderWithCallback(folderId: number, callbackFunction: any): void {
        if (this.tree != null) {
            let folder: Folder =
                FolderFormInstanceTreeManager.lookupFolder(this.tree, folderId);

            callbackFunction(folder);
        } else {
            let folderRequest: TreeFolderRequest =
                new TreeFolderRequest(this.treeObservable, folderId);
            let observable: Observable<FolderFormInstanceTree> = folderRequest.getFolder();

            observable.subscribe(folder => {
                callbackFunction(folder);
            });
        }

        return;
    }

    // Here is what a call to getFolder() would look like (though this method is presently not being used):
    //
    //    this.treeManager.getFolder(folderId).subscribe(folderFormInstanceTree => {
    //         console.log('StructureComponent:  retrieved a folder as a folder/form instance tree.');
    //     });
    public getFolder(folderId: number): Observable<FolderFormInstanceTree> {
        let folderRequest: TreeFolderRequest =
            new TreeFolderRequest(this.treeObservable, folderId);
        let result: Observable<FolderFormInstanceTree> = folderRequest.getFolder();

        return (result);
    }

    // Here is what a call to getFolder2() would look like (though it is not being used):
    //
    //    this.treeManager.getFolder2(folderId).subscribe(folderFormInstanceTree => {
    //        console.log('StructureComponent:  retrieved a folder as a folder/form instance tree a second way.');
    //    });
    public getFolder2(folderId: number): Observable<FolderFormInstanceTree> {
        let result: Observable<FolderFormInstanceTree> = null;

        result = Observable.create(function (observer: any) {
            let treeObservable: Observable<FolderFormInstanceTree> = this['treeObservable'];

            treeObservable.subscribe(tree => {
                let folder: Folder = FolderFormInstanceTreeManager.lookupFolder(tree, folderId);

                observer.next(folder);
            });
        });
        result['treeObservable'] = this.treeObservable;

        return (result);
    }

    // Note:  as getFormInstanceWithCallback() is the easiest to call, it is
    //        presently being used within the structure component class.
    public getFormInstanceWithCallback(parentFolderId: number, formInstanceId: number, callbackFunction: any): void {
        if (this.tree != null) {
            let formInstance: FormInstance =
                FolderFormInstanceTreeManager.lookupFormInstance(this.tree, parentFolderId, formInstanceId);

            callbackFunction(formInstance);
        } else {
            let formInstanceRequest: TreeFormInstanceRequest =
                new TreeFormInstanceRequest(this.treeObservable, parentFolderId, formInstanceId);
            let observable: Observable<FolderFormInstanceTree> = formInstanceRequest.getFormInstance();

            observable.subscribe(formInstance => {
                callbackFunction(formInstance);
            });
        }

        return;
    }

    // Here is what a call to getFormInstance() would look like (though it is not being used):
    // 
    //    this.treeManager.getFormInstance(this.currentFolder.id, formInstanceId).subscribe(folderFormInstanceTree => {
    //        console.log('StructureComponent:  retrieved a formInstance as a folder/form instance tree.');
    //    });
    public getFormInstance(parentFolderId: number, formInstanceId: number): Observable<FolderFormInstanceTree> {
        let formInstanceRequest: TreeFormInstanceRequest =
            new TreeFormInstanceRequest(this.treeObservable, parentFolderId, formInstanceId);
        let result: Observable<FolderFormInstanceTree> = formInstanceRequest.getFormInstance();

        return (result);
    }

    // Helper methods.
    public static lookupFolder(tree: FolderFormInstanceTree, folderId: number): Folder {
        let treeObject: FolderFormInstanceTree =
            FolderFormInstanceTreeManager.lookupChildFolder(tree.childFolders, folderId);
        let folder: Folder = (treeObject != null ? treeObject.toFolder() : null);

        return (folder);
    }

    private static lookupChildFolder(childFolders: FolderFormInstanceTree[], folderId: number): FolderFormInstanceTree {
        let folder: FolderFormInstanceTree = null;

        for (let iChild: number = 0; iChild < childFolders.length; iChild++) {
            let childFolder: FolderFormInstanceTree = childFolders[iChild];

            if (childFolder.id == folderId) {
                folder = childFolder;

                break;
            } else if ((childFolder.childFolders != null) && (childFolder.childFolders.length > 0)) {
                folder = FolderFormInstanceTreeManager.lookupChildFolder(childFolder.childFolders, folderId);

                if (folder != null) {
                    break;
                }
            }
        }

        return (folder);
    }

    public static lookupFormInstance(tree: FolderFormInstanceTree,
        parentFolderId: number,
        formInstanceId: number): FormInstance {
        let treeObject: FolderFormInstanceTree = null;

        let parentFolder: FolderFormInstanceTree =
            FolderFormInstanceTreeManager.lookupChildFolder(tree.childFolders, parentFolderId);

        if ((parentFolder != null) && (parentFolder.childFormInstances != null)) {
            for (let iChildFI: number = 0; iChildFI < parentFolder.childFormInstances.length; iChildFI++) {
                let childFormInstance: FolderFormInstanceTree = parentFolder.childFormInstances[iChildFI];

                if (childFormInstance.id == formInstanceId) {
                    treeObject = childFormInstance;

                    break;
                }
            }
        }

        let formInstance: FormInstance = (treeObject != null ? treeObject.toFormInstance() : null);

        return (formInstance);
    }

}

class TreeFolderRequest {
    private treeObservable: Observable<FolderFormInstanceTree> = null;
    private folderId: number = -1;

    public constructor(treeObservable: Observable<FolderFormInstanceTree>,
                       folderId: number) {
        this.treeObservable = treeObservable;
        this.folderId = folderId;

        return;
    }

    public getFolder(): Observable<FolderFormInstanceTree> {
        let result: Observable<FolderFormInstanceTree> = Observable.create(this.lookupFolderArrowFunction);

        return (result);
    }

    public lookupFolderArrowFunction = (observer: any): void => {
        this.treeObservable.subscribe(tree => {
            let folder: Folder =
                FolderFormInstanceTreeManager.lookupFolder(tree, this.folderId);

            observer.next(folder);
        });

        return;
    }
}

class TreeFormInstanceRequest {
    private treeObservable: Observable<FolderFormInstanceTree> = null;
    private parentFolderId: number = -1;
    private formInstanceId: number = -1;

    public constructor(treeObservable: Observable<FolderFormInstanceTree>, parentFolderId: number, formInstanceId: number) {
        this.treeObservable = treeObservable;
        this.parentFolderId = parentFolderId;
        this.formInstanceId = formInstanceId;

        return;
    }

    public getFormInstance(): Observable<FolderFormInstanceTree> {
        let result: Observable<FolderFormInstanceTree> = Observable.create(this.lookupFormInstanceArrowFunction);

        return (result);
    }

    public lookupFormInstanceArrowFunction = (observer: any): void => {
        this.treeObservable.subscribe(tree => {
            let formInstance: FormInstance =
                FolderFormInstanceTreeManager.lookupFormInstance(tree, this.parentFolderId, this.formInstanceId);

            observer.next(formInstance);
        });

        return;
    }
}
